import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import HostAgreementDocument from '../../../components/HostAgreementDocument/HostAgreementDocument';
import { getUserAccessToken, getUserSession, persistUserSession } from '../../../services/userSession';
import { getScreenHostAccount } from '../../../services/getScreenHostAccount';
import { acceptScreenHostAgreement } from '../../../services/acceptScreenHostAgreement';
import { getHostAgreementMe } from '../../../services/getHostAgreementMe';
import { loadAndPersistScreenHostTeamContext } from '../../../services/resolveScreenHostContext';
import { isTeamDelegateSession } from '../../../services/screenHostTeam';
import '../ScreenHostLogin/ScreenHostLogin.css';
import './ScreenHostAccept.css';
import '../../../components/HostAgreementDocument/HostAgreementDocument.css';

export default function ScreenHostAccept() {
    const navigate = useNavigate();
    const location = useLocation();
    const routeSession = location.state || {};
    const sessionToken = getUserSession()?.token;
    const token = routeSession.token || getUserAccessToken() || sessionToken;
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [emailWarning, setEmailWarning] = useState('');
    const [agreed, setAgreed] = useState(false);
    const [legalName, setLegalName] = useState('');
    const [businessName, setBusinessName] = useState('');
    const [accountName, setAccountName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [userId, setUserId] = useState(null);
    const [agreementVersion, setAgreementVersion] = useState(null);
    const [esignNotice, setEsignNotice] = useState('');
    const [isTeamDelegate, setIsTeamDelegate] = useState(false);

    useEffect(() => {
        if (!token) {
            navigate('/screenHostLogin/', { replace: true });
            return;
        }

        if (routeSession.token) {
            persistUserSession({
                token: routeSession.token,
                refreshToken: routeSession.refreshToken,
                userId: routeSession.userId,
                phoneNumber: routeSession.phoneNumber
            });
        }

        Promise.all([
            getScreenHostAccount(token),
            loadAndPersistScreenHostTeamContext(token, persistUserSession).catch(() => null),
        ])
            .then(([response]) => {
                const data = response.data;
                if (data.accountActivated) {
                    navigate('/user/compose', { replace: true });
                    return null;
                }
                setUserId(data.userId);
                setBusinessName(data.businessName || '');
                setAccountName([data.fname, data.lname].filter(Boolean).join(' ').trim());
                setPhoneNumber(data.phoneNumber || '');
                const session = getUserSession() || routeSession;
                setIsTeamDelegate(isTeamDelegateSession({
                    ...session,
                    teamMemberOnly: Boolean(data.teamMemberOnly),
                }));
                return getHostAgreementMe(token);
            })
            .then((agreementRes) => {
                if (!agreementRes) {
                    return;
                }
                if (agreementRes.data?.success) {
                    setAgreementVersion(agreementRes.data.version);
                    setEsignNotice(agreementRes.data.esignNotice || '');
                }
                setLoading(false);
            })
            .catch((err) => {
                const apiError = err.response?.data?.error;
                if (apiError === 'Invalid token' || apiError === 'No credentials sent!') {
                    setError('Your session expired. Please sign in again.');
                } else {
                    setError('Could not load your account. Please sign in again.');
                }
                setLoading(false);
            });
    }, [token, navigate, routeSession.refreshToken, routeSession.token, routeSession.userId, routeSession.phoneNumber]);

    function onSubmit(event) {
        event.preventDefault();
        setError('');
        setEmailWarning('');

        if (!agreed) {
            setError(isTeamDelegate
                ? 'You must confirm that you agree to the terms.'
                : 'You must confirm that you have read and agree to the agreement.');
            return;
        }

        const resolvedLegalName = isTeamDelegate ? accountName.trim() : legalName.trim();
        if (!resolvedLegalName) {
            setError(isTeamDelegate
                ? 'Could not verify your account name. Please sign in again.'
                : 'Please type your full legal name.');
            return;
        }

        if (!agreementVersion) {
            setError('Could not verify agreement version. Please refresh and try again.');
            return;
        }

        setSubmitting(true);
        acceptScreenHostAgreement(token, {
            legalName: resolvedLegalName,
            agreementAccepted: true,
            agreementVersion
        })
            .then((response) => {
                if (response.data.emailWarning) {
                    setEmailWarning(response.data.emailWarning);
                }
                const session = getUserSession() || {};
                const resolvedUserId = userId || session.userId;
                persistUserSession({
                    token,
                    userId: resolvedUserId,
                    phoneNumber
                });
                navigate('/user/compose', {
                    replace: true,
                    state: {
                        userId: resolvedUserId,
                        phoneNumber,
                        token
                    }
                });
            })
            .catch((err) => {
                const msg =
                    err.response?.data?.error ||
                    err.response?.data?.message ||
                    'Could not record your agreement. Please try again.';
                setError(msg);
                setSubmitting(false);
            });
    }

    if (loading) {
        return (
            <div className="screen-host-login-page">
                <main className="screen-host-login-main">
                    <p className="screen-host-accept-loading">Loading agreement…</p>
                </main>
            </div>
        );
    }

    const canSubmit = agreed && (isTeamDelegate ? accountName.trim().length > 0 : legalName.trim().length > 0) && !submitting;

    return (
        <div className="screen-host-login-page screen-host-accept-page">
            <nav className="screen-host-login-nav">
                <button type="button" className="screen-host-login-brand" onClick={() => navigate('/')}>
                    INVERT
                </button>
            </nav>
            <main className="screen-host-accept-main">
                <form className="screen-host-accept-card" onSubmit={onSubmit}>
                    <h1>{isTeamDelegate ? 'Accept team access terms' : 'Accept screen host agreement'}</h1>
                    <p className="screen-host-login-subtitle">
                        {isTeamDelegate
                            ? `Confirm that you agree to INVERT's terms before managing screens${agreementVersion ? ` (version ${agreementVersion})` : ''}.`
                            : `Please read the agreement below, type your legal name, and click I Agree to activate your account${agreementVersion ? ` (version ${agreementVersion})` : ''}.`}
                    </p>

                    {!isTeamDelegate ? (
                        <div className="host-agreement-scroll-panel">
                            <HostAgreementDocument businessName={businessName} compact personalized authToken={token} />
                        </div>
                    ) : null}

                    {!isTeamDelegate && esignNotice ? (
                        <p className="host-agreement-esign host-agreement-esign--inline">{esignNotice}</p>
                    ) : null}

                    <label className="screen-host-accept-checkbox">
                        <input
                            type="checkbox"
                            checked={agreed}
                            onChange={(e) => {
                                setAgreed(e.target.checked);
                                if (e.target.checked) {
                                    setError('');
                                }
                            }}
                            required
                        />
                        <span>
                            {isTeamDelegate ? (
                                <>
                                    I agree to the INVERT screen host terms and conditions
                                    {agreementVersion ? ` (version ${agreementVersion})` : ''}.
                                </>
                            ) : (
                                <>
                                    I have read and agree to the{' '}
                                    <Link to="/screen-host-agreement" target="_blank" rel="noopener noreferrer">
                                        Storefront Screen Hosting &amp; Advertising Agreement
                                    </Link>{' '}
                                    {agreementVersion ? `(version ${agreementVersion})` : ''}.
                                </>
                            )}
                        </span>
                    </label>

                    {!isTeamDelegate ? (
                        <div className="screen-host-accept-legal-field">
                            <label htmlFor="legal-name">Full legal name</label>
                            <input
                                id="legal-name"
                                className="screen-host-accept-legal-input"
                                type="text"
                                required
                                maxLength={200}
                                autoComplete="name"
                                placeholder="Type your full legal name"
                                value={legalName}
                                onChange={(e) => setLegalName(e.target.value)}
                            />
                            <p className="screen-host-accept-legal-hint">
                                Type your name exactly as you are signing this agreement.
                            </p>
                        </div>
                    ) : null}

                    {error && <p className="screen-host-login-error">{error}</p>}
                    {emailWarning && <p className="screen-host-accept-warning">{emailWarning}</p>}

                    <button type="submit" className="screen-host-login-submit" disabled={!canSubmit}>
                        {submitting ? 'Recording…' : 'I Agree'}
                    </button>
                </form>
            </main>
        </div>
    );
}
